import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Truck, Ship, Boxes, Warehouse, Handshake, FileCheck2, ArrowUpRight } from "lucide-react";
import { useLang } from "@/contexts/LanguageContext";
import { gsap, ScrollTrigger, useGsap } from "@/lib/motion";
import { HEX_PATH, BOX_PATH } from "@/components/brand/Logo";

const ICONS = [Truck, Ship, Boxes, Warehouse, Handshake, FileCheck2];

/* Services read like a spec sheet that comes alive: each title fills with ink
   as you scroll past it, and a sticky panel beside the list shows the service
   you're reading, its icon framed by the logo's hexagon redrawing itself. */
export default function ServicesSection() {
  const { t, lang } = useLang();
  const root = useRef(null);
  const visual = useRef(null);
  const isRtl = lang === "ar";
  const items = t.services.items;
  const [active, setActive] = useState(0);
  const ActiveIcon = ICONS[active] || Boxes;

  useGsap(
    () => {
      gsap.from(".svc-head > *", {
        y: 24,
        opacity: 0,
        duration: 0.9,
        stagger: 0.1,
        scrollTrigger: { trigger: root.current, start: "top 75%", once: true },
      });
      gsap.utils.toArray(".svc-row").forEach((row, i) => {
        gsap.fromTo(
          row.querySelector(".svc-fill"),
          { clipPath: isRtl ? "inset(0 0 0 100%)" : "inset(0 100% 0 0)" },
          {
            clipPath: "inset(0 0% 0 0%)",
            ease: "none",
            scrollTrigger: { trigger: row, start: "top 85%", end: "top 45%", scrub: true },
          },
        );
        gsap.from(row.querySelector(".svc-icon"), {
          scale: 0.4,
          rotate: -30,
          opacity: 0,
          duration: 0.8,
          ease: "back.out(1.6)",
          scrollTrigger: { trigger: row, start: "top 80%", once: true },
        });
        ScrollTrigger.create({
          trigger: row,
          start: "top 55%",
          end: "bottom 55%",
          onToggle: (self) => self.isActive && setActive(i),
        });
      });
    },
    [lang],
    root,
  );

  // Each change of service: the hexagon redraws and the icon lands inside it.
  useGsap(
    () => {
      const hex = visual.current.querySelector(".svc-hex");
      const len = hex.getTotalLength();
      gsap.fromTo(hex, { strokeDasharray: len, strokeDashoffset: len }, { strokeDashoffset: 0, duration: 1.1, ease: "power3.inOut" });
      gsap.from(".svc-box", { scale: 0.6, opacity: 0, transformOrigin: "50% 50%", duration: 0.7, delay: 0.25, ease: "back.out(1.6)" });
      gsap.from(".svc-big-icon", { scale: 0.6, rotate: -20, opacity: 0, duration: 0.7, delay: 0.35, ease: "expo.out" });
      gsap.from(".svc-big-label", { y: 14, opacity: 0, filter: "blur(4px)", duration: 0.6, ease: "expo.out" });
    },
    [active, lang],
    visual,
  );

  return (
    <section ref={root} data-testid="services-section" className="relative bg-[var(--surface)] text-[var(--ink)] py-28 lg:py-36">
      <div className="container-x grid lg:grid-cols-12 gap-12 lg:gap-16">
        <div className="lg:col-span-4">
          <div className="lg:sticky lg:top-28">
            <div className="svc-head">
              <p className="text-[15px] font-semibold text-[var(--accent-ink)]">{t.services.eyebrow}</p>
              <h2 className="type-display mt-4 text-[clamp(2rem,3.6vw,3.25rem)] max-w-[12ch]">{t.services.title}</h2>
              <p className="mt-5 text-lg leading-relaxed text-[var(--muted-ink)] max-w-[34ch]">{t.services.subtitle}</p>
              <Link
                to="/services"
                className="mt-8 inline-flex items-center gap-2 font-bold text-[var(--accent-ink)] underline-offset-4 hover:underline"
              >
                {t.common.exploreServices}
                <ArrowUpRight className={`w-4 h-4 ${isRtl ? "-scale-x-100" : ""}`} aria-hidden />
              </Link>
            </div>

            {/* The service you're reading, large */}
            <div ref={visual} aria-hidden className="hidden lg:block mt-12 on-dark relative overflow-hidden rounded-[1.75rem] bg-[var(--forest-900)] p-8">
              <div className="absolute inset-0 dot-grid opacity-50" style={{ zIndex: -1 }} />
              <div className="relative flex items-center gap-6" style={{ zIndex: 1 }}>
                {/* The full logo mark: the "M" outline holding the parcel box. The
                    service icon sits inside the box, clear of the M's inner V. */}
                <div className="relative w-32 shrink-0 aspect-[692/796]" style={{ zIndex: 2 }}>
                  <svg viewBox="-10 -10 692 796" className="absolute inset-0 w-full h-full" style={{ zIndex: 1 }}>
                    {/* Background fill to block dot-grid */}
                    <path className="svc-hex-bg" d={HEX_PATH} fill="var(--forest-900)" stroke="none" />
                    {/* Stroke outline */}
                    <path className="svc-hex" d={HEX_PATH} fill="none" stroke="oklch(0.9 0.19 122)" strokeWidth="18" strokeLinejoin="round" />
                    <path className="svc-box" d={BOX_PATH} fill="oklch(0.9 0.19 122)" />
                  </svg>
                  {/* Centre of the box's flat body: (335, 591) in the mark's coordinates. */}
                  <div className="absolute -translate-x-1/2 -translate-y-1/2" style={{ left: `${(345 / 692) * 100}%`, top: `${(601 / 796) * 100}%`, zIndex: 2 }}>
                    <ActiveIcon
                      className={`svc-big-icon block w-6 h-6 text-[var(--signal-ink)] ${active === 0 && isRtl ? "rtl-flip" : ""}`}
                      strokeWidth={2.25}
                    />
                  </div>
                </div>
                <div className="svc-big-label min-w-0">
                  <div className="type-num text-sm font-semibold text-[var(--signal)]">
                    {active + 1} / {items.length}
                  </div>
                  <div className="mt-1 text-xl font-extrabold leading-snug text-[var(--on-dark)]">{items[active]?.t}</div>
                </div>
              </div>
              <div className="relative mt-6 flex gap-1.5" style={{ zIndex: 1 }}>
                {items.map((s, i) => (
                  <span key={s.t} className={`h-1 flex-1 rounded-full transition-colors duration-500 ${i <= active ? "bg-[var(--signal)]" : "bg-white/15"}`} />
                ))}
              </div>
            </div>
          </div>
        </div>

        <ul className="svc-list lg:col-span-8 border-b border-[var(--line)]">
          {items.map((s, i) => {
            const Icon = ICONS[i] || Boxes;
            return (
              <li key={s.t} className="svc-row group relative border-t border-[var(--line)] overflow-hidden">
                <span
                  aria-hidden
                  className="absolute inset-0 bg-[var(--forest-800)] scale-x-0 group-hover:scale-x-100 origin-left rtl:origin-right transition-transform duration-700 ease-out-expo"
                />
                <div className="relative flex items-start gap-5 sm:gap-8 px-1 sm:px-4 py-8 sm:py-10 transition-colors duration-500 group-hover:text-[var(--on-dark)]">
                  <span
                    className={`svc-icon shrink-0 mt-1 w-12 h-12 rounded-2xl grid place-items-center transition-colors duration-500 group-hover:bg-[var(--signal)] group-hover:text-[var(--signal-ink)] ${
                      i === active ? "bg-[var(--forest-800)] text-[var(--signal)]" : "bg-[var(--bg)] text-[var(--accent-ink)]"
                    }`}
                  >
                    <Icon className={`w-5 h-5 ${i === 0 && isRtl ? "rtl-flip" : ""}`} aria-hidden />
                  </span>
                  <div className="min-w-0 flex-1">
                    <h3 className="relative type-wide text-xl sm:text-2xl lg:text-[1.9rem] font-extrabold leading-tight">
                      <span className="text-[var(--muted-ink)] group-hover:text-[var(--on-dark)] transition-colors duration-500">{s.t}</span>
                      <span aria-hidden className="svc-fill absolute inset-0 text-[var(--ink)] group-hover:text-[var(--on-dark)] transition-colors duration-500">
                        {s.t}
                      </span>
                    </h3>
                    <p className="mt-2 text-[15px] sm:text-base leading-relaxed text-[var(--muted-ink)] transition-colors duration-500 group-hover:text-[var(--on-dark-muted)] max-w-[52ch]">
                      {s.d}
                    </p>
                  </div>
                  <ArrowUpRight
                    className={`hidden sm:block shrink-0 w-6 h-6 mt-2 opacity-0 transition-all duration-500 ease-out-expo text-[var(--signal)] group-hover:opacity-100 ${
                      isRtl ? "-scale-x-100" : ""
                    }`}
                    aria-hidden
                  />
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
}
